import { supabase } from './supabase';
import { createLogger } from './logger';
import { collaboratorService } from './collaboratorService';

const log = createLogger('collaboration');

/**
 * 協作權限管理服務 - 簡化版
 * 基於現有 collaboratorService 擴展，實現基本的權限檢查
 */
export const collaborationPermissions = {
  // 權限檢查結果快取 (5分鐘TTL)
  permissionCache: new Map<string, { result: boolean; expires: number }>(),

  /**
   * 簡化版權限檢查 - 只檢查是否為協作者
   * @param userId 用戶ID
   * @param invitationId 邀請函ID
   */
  async canEdit(userId: string, invitationId: string): Promise<boolean> {
    // 建立快取 key
    const cacheKey = `edit:${userId}:${invitationId}`;
    
    // 檢查快取
    const cached = this.getCachedPermission(cacheKey);
    if (cached !== null) {
      return cached;
    }

    try {
      // 1. 檢查是否為 owner
      const isOwner = await this.isOwner(userId, invitationId);
      if (isOwner) {
        this.setCachedPermission(cacheKey, true);
        return true;
      }
      
      // 2. 檢查是否為協作者
      const isCollaborator = await collaboratorService.isCollaborator(userId, invitationId);
      
      // 快取結果
      this.setCachedPermission(cacheKey, isCollaborator);
      return isCollaborator;
    } catch (error) {
      log.error('權限檢查錯誤:', error);
      return false;
    }
  },

  /**
   * 檢查是否為邀請函 owner
   * @param userId 用戶ID
   * @param invitationId 邀請函ID
   */
  async isOwner(userId: string, invitationId: string): Promise<boolean> {
    const cacheKey = `owner:${userId}:${invitationId}`;
    
    // 檢查快取
    const cached = this.getCachedPermission(cacheKey);
    if (cached !== null) {
      return cached;
    }

    try {
      const { data, error } = await supabase
        .from('wedding_invitations')
        .select('user_id')
        .eq('id', invitationId)
        .single();
      
      if (error) {
        log.error('檢查 owner 錯誤:', error);
        return false;
      }

      const result = data?.user_id === userId;
      this.setCachedPermission(cacheKey, result);
      return result;
    } catch (error) {
      log.error('檢查 owner 錯誤:', error);
      return false;
    }
  },

  /**
   * 檢查是否可以檢視邀請函 (owner 或協作者)
   * @param userId 用戶ID
   * @param invitationId 邀請函ID
   */
  async canView(userId: string, invitationId: string): Promise<boolean> {
    // 目前簡化版本：能編輯就能檢視
    return await this.canEdit(userId, invitationId);
  },

  /**
   * 更新協作元數據
   * @param tableName 表格名稱
   * @param recordId 記錄ID
   * @param userId 用戶ID
   * @param invitationId 邀請函ID
   */
  async updateCollaborationMetadata(
    tableName: string,
    recordId: string,
    userId: string,
    invitationId: string
  ): Promise<void> {
    try {
      const { error } = await supabase
        .rpc('update_collaboration_metadata', {
          table_name_param: tableName,
          record_id_param: recordId,
          user_id_param: userId,
          invitation_id_param: invitationId
        });

      if (error) {
        log.error('更新協作元數據錯誤:', error);
      }
    } catch (error) {
      log.error('更新協作元數據錯誤:', error);
    }
  },

  /**
   * 檢查是否有編輯衝突 (30秒內的修改)
   * @param tableName 表格名稱
   * @param recordId 記錄ID
   * @param currentUserId 當前用戶ID
   */
  async checkEditConflict(
    tableName: string,
    recordId: string,
    currentUserId: string
  ): Promise<{
    hasConflict: boolean;
    lastEditor?: string;
    lastModified?: Date;
  }> {
    try {
      const { data, error } = await supabase
        .from('collaboration_metadata')
        .select(`
          last_modified_by,
          last_modified_at
        `)
        .eq('table_name', tableName)
        .eq('record_id', recordId)
        .single();

      if (error || !data) {
        return { hasConflict: false };
      }

      const lastModified = new Date(data.last_modified_at);
      const now = new Date();
      const timeDiff = now.getTime() - lastModified.getTime();
      
      // 檢查是否在 30 秒內被其他用戶修改
      const hasConflict = timeDiff < 30000 && data.last_modified_by !== currentUserId;

      return {
        hasConflict,
        lastEditor: data.last_modified_by,
        lastModified
      };
    } catch (error) {
      log.error('檢查編輯衝突錯誤:', error);
      return { hasConflict: false };
    }
  },

  /**
   * 取得快取的權限結果
   * @param key 快取鍵
   */
  getCachedPermission(key: string): boolean | null {
    const cached = this.permissionCache.get(key);
    if (cached && cached.expires > Date.now()) {
      return cached.result;
    }
    this.permissionCache.delete(key);
    return null;
  },

  /**
   * 設定權限快取
   * @param key 快取鍵
   * @param result 權限結果
   * @param ttl 過期時間 (毫秒，預設5分鐘)
   */
  setCachedPermission(key: string, result: boolean, ttl: number = 300000): void {
    this.permissionCache.set(key, {
      result,
      expires: Date.now() + ttl
    });
  },

  /**
   * 清除權限快取
   * @param userId 用戶ID (可選，不提供則清除所有)
   */
  clearPermissionCache(userId?: string): void {
    if (userId) {
      // 清除特定用戶的快取
      for (const [key] of this.permissionCache) {
        if (key.includes(userId)) {
          this.permissionCache.delete(key);
        }
      }
    } else {
      // 清除所有快取
      this.permissionCache.clear();
    }
  }
};